import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function Login() {
  const navigate = useNavigate();
  const shadowHostRef = useRef(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Track step: 1 = Credentials, 2 = OTP Verification
  const [step, setStep] = useState(1);
  const [storedUsername, setStoredUsername] = useState('');

  useEffect(() => {
    if (!shadowHostRef.current) return;

    let shadowRoot = shadowHostRef.current.shadowRoot;
    if (!shadowRoot) {
      shadowRoot = shadowHostRef.current.attachShadow({ mode: 'open' });
    }

    // Render Form dynamically based on Step
    if (step === 1) {
      shadowRoot.innerHTML = `
        <style>
          * { box-sizing: border-box; font-family: system-ui, -apple-system, sans-serif; }
          .form-box {
            background: #1e293b;
            border: 1px solid #334155;
            padding: 24px;
            border-radius: 8px;
            width: 100%;
            max-width: 380px;
            color: #f8fafc;
            margin: 0 auto;
          }
          .form-group { margin-bottom: 16px; }
          label { display: block; font-size: 13px; margin-bottom: 6px; color: #94a3b8; }
          input {
            width: 100%;
            padding: 10px 12px;
            border-radius: 6px;
            border: 1px solid #475569;
            background: #0f172a;
            color: #fff;
            font-size: 14px;
            outline: none;
          }
          input:focus { border-color: #3b82f6; }
          button {
            width: 100%;
            padding: 10px;
            background: #2563eb;
            color: #fff;
            border: none;
            border-radius: 6px;
            font-weight: 600;
            cursor: pointer;
            margin-top: 8px;
          }
          button:hover { background: #1d4ed8; }
        </style>
        <div class="form-box">
          <h3 style="margin-top:0; text-align:center; margin-bottom: 20px;">Admin Login</h3>
          <form id="shadow-cred-form">
            <div class="form-group">
              <label>Username</label>
              <input type="text" id="user-input" required autocomplete="off" />
            </div>
            <div class="form-group">
              <label>Password</label>
              <input type="password" id="pass-input" required autocomplete="current-password" />
            </div>
            <button type="submit">Send OTP</button>
          </form>
        </div>
      `;

      const form = shadowRoot.getElementById('shadow-cred-form');

      const handleCredSubmit = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        const username = shadowRoot.getElementById('user-input')?.value?.trim();
        const password = shadowRoot.getElementById('pass-input')?.value?.trim();

        if (!username || !password) return;

        setError('');
        setLoading(true);

        try {
          const res = await fetch('https://sanjaydhir.com/api/admin/login-request-otp', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username, password }),
          });

          const data = await res.json();
          if (!res.ok || !data.success) {
            throw new Error(data.message || 'Invalid credentials');
          }

          setStoredUsername(username);
          setStep(2); // Switch to OTP view
        } catch (err) {
          setError(err.message);
        } finally {
          setLoading(false);
        }
      };

      form.addEventListener('submit', handleCredSubmit);
      return () => {
        form.removeEventListener('submit', handleCredSubmit);
      };

    } else if (step === 2) {
      shadowRoot.innerHTML = `
        <style>
          * { box-sizing: border-box; font-family: system-ui, -apple-system, sans-serif; }
          .form-box {
            background: #1e293b;
            border: 1px solid #334155;
            padding: 24px;
            border-radius: 8px;
            width: 100%;
            max-width: 380px;
            color: #f8fafc;
            margin: 0 auto;
          }
          .form-group { margin-bottom: 16px; }
          .info-text { font-size: 13px; color: #94a3b8; text-align: center; margin-bottom: 16px; }
          input {
            width: 100%;
            padding: 10px 12px;
            border-radius: 6px;
            border: 1px solid #0284c7;
            background: #0f172a;
            color: #fff;
            font-size: 20px;
            text-align: center;
            letter-spacing: 4px;
            outline: none;
          }
          input:focus { border-color: #38bdf8; }
          button.verify-btn {
            width: 100%;
            padding: 10px;
            background: #059669;
            color: #fff;
            border: none;
            border-radius: 6px;
            font-weight: 600;
            cursor: pointer;
            margin-top: 8px;
          }
          button.verify-btn:hover { background: #047857; }
          button.back-btn {
            width: 100%;
            padding: 8px;
            background: transparent;
            color: #94a3b8;
            border: none;
            font-size: 13px;
            cursor: pointer;
            margin-top: 6px;
          }
          button.back-btn:hover { color: #f8fafc; }
        </style>
        <div class="form-box">
          <h3 style="margin-top:0; text-align:center; margin-bottom: 8px;">2-Step Verification</h3>
          <p class="info-text">Enter the 6-digit OTP dispatched to your registered contact.</p>
          <form id="shadow-otp-form">
            <div class="form-group">
              <input type="text" id="otp-input" maxlength="6" placeholder="••••••" required autofocus />
            </div>
            <button type="submit" class="verify-btn">Verify & Enter Dashboard</button>
            <button type="button" id="back-btn" class="back-btn">Back to Login</button>
          </form>
        </div>
      `;

      const otpForm = shadowRoot.getElementById('shadow-otp-form');
      const backBtn = shadowRoot.getElementById('back-btn');

      const handleOtpSubmit = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        const otp = shadowRoot.getElementById('otp-input')?.value?.trim();
        if (!otp) return;

        setError('');
        setLoading(true);

        try {
          const res = await fetch('https://sanjaydhir.com/api/admin/verify-login-otp', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username: storedUsername, otp }),
          });

          const data = await res.json();
          if (!res.ok || !data.token) {
            throw new Error(data.message || 'Invalid or expired OTP');
          }

          localStorage.setItem('adminToken', data.token);
          navigate('/admin/events');
        } catch (err) {
          setError(err.message);
        } finally {
          setLoading(false);
        }
      };

      const handleBack = () => {
        setError('');
        setStep(1);
      };

      otpForm.addEventListener('submit', handleOtpSubmit);
      backBtn.addEventListener('click', handleBack);

      return () => {
        otpForm.removeEventListener('submit', handleOtpSubmit);
        backBtn.removeEventListener('click', handleBack);
      };
    }
  }, [step, storedUsername, navigate]);

  return (
    <div style={{ minHeight: '80vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '20px' }}>
      {error && (
        <div style={{ background: '#7f1d1d', color: '#fecaca', padding: '10px 16px', borderRadius: '6px', marginBottom: '16px', maxWidth: '380px', width: '100%', fontSize: '14px' }}>
          {error}
        </div>
      )}
      {loading && <div style={{ color: '#38bdf8', marginBottom: '12px', fontSize: '14px' }}>Verifying...</div>}
      <div ref={shadowHostRef} style={{ width: '100%' }} />
    </div>
  );
}